import { useEffect, useMemo, useRef, useState } from "react";
import type { MetricCampaign } from "../api";
import { compact, shortDate } from "./format";

/**
 * Which campaigns the page is looking at: all of them, or any subset.
 *
 * Grouped by the domain each campaign runs under, and a domain is picked by
 * ticking its row — which selects its campaigns. That is the only way a
 * domain reaches the server: invariant 37 gives no endpoint a domain
 * parameter, so the request carries campaign ids, which narrow within the
 * caller's scope and can never widen it.
 *
 * Edits are a draft until Apply, so ticking six boxes costs one fetch
 * rather than six, and closing the panel without applying changes nothing.
 *
 * `selected` is `null` for every campaign. Choosing all of them explicitly
 * collapses back to `null`, so a campaign that starts running tomorrow is
 * included in "all" rather than silently left out of a stale list.
 */
export default function CampaignFilter({
  campaigns,
  selected,
  onChange,
}: {
  campaigns: MetricCampaign[];
  selected: string[] | null;
  onChange: (ids: string[] | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);

  const all = useMemo(() => campaigns.map((c) => c.campaign_id), [campaigns]);
  const current = useMemo(() => new Set(selected ?? all), [selected, all]);

  // Close on a click outside or Escape, discarding the draft.
  useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", click);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", click);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const by = new Map<string, { label: string; region: string; items: MetricCampaign[] }>();
    for (const c of campaigns) {
      const hay = `${c.campaign_identifier ?? ""} ${c.campaign_name ?? ""} ${c.domain_identifier ?? ""}`;
      if (q && !hay.toLowerCase().includes(q)) continue;
      // (region, domain_id), never domain_id alone -- invariant 34.
      const key = `${c.region}:${c.domain_id}`;
      if (!by.has(key)) {
        by.set(key, { label: c.domain_identifier ?? c.domain_id, region: c.region, items: [] });
      }
      by.get(key)!.items.push(c);
    }
    return [...by.values()];
  }, [campaigns, query]);

  const regions = new Set(campaigns.map((c) => c.region));

  const toggle = (ids: string[], on: boolean) => {
    const next = new Set(draft);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setDraft(next);
  };

  const apply = () => {
    setOpen(false);
    const ids = all.filter((id) => draft.has(id));
    onChange(ids.length === all.length ? null : ids);
  };

  const label =
    selected === null
      ? `All campaigns (${campaigns.length})`
      : selected.length === 1
        ? name(campaigns.find((c) => c.campaign_id === selected[0])) ?? "1 campaign"
        : `${selected.length} of ${campaigns.length} campaigns`;

  return (
    <div className="cfilter" ref={box}>
      <button
        className={selected ? "cfilter-btn on" : "cfilter-btn"}
        aria-expanded={open}
        onClick={() => {
          if (!open) {
            setDraft(new Set(current));
            setQuery("");
          }
          setOpen(!open);
        }}
      >
        {label} ▾
      </button>
      {selected && (
        <button className="btn tiny" onClick={() => onChange(null)}>
          Show all
        </button>
      )}

      {open && (
        <div className="cfilter-panel" role="dialog" aria-label="Choose campaigns">
          <div className="cfilter-top">
            <input
              type="search"
              placeholder="Search campaigns or domains"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <button className="btn tiny" onClick={() => setDraft(new Set(all))}>
              All
            </button>
            <button className="btn tiny" onClick={() => setDraft(new Set())}>
              None
            </button>
          </div>

          <div className="cfilter-list">
            {groups.length === 0 && <div className="sub dim">Nothing matches.</div>}
            {groups.map((g) => {
              const ids = g.items.map((c) => c.campaign_id);
              const n = ids.filter((id) => draft.has(id)).length;
              return (
                <div key={`${g.region}:${g.label}`} className="cfilter-group">
                  <label className="cfilter-domain">
                    <Check
                      checked={n === ids.length}
                      indeterminate={n > 0 && n < ids.length}
                      onChange={(on) => toggle(ids, on)}
                    />
                    <span className="cid">{g.label}</span>
                    {regions.size > 1 && <span className="tz">{g.region}</span>}
                    <span className="dim cfilter-n">
                      {n}/{ids.length}
                    </span>
                  </label>
                  {g.items.map((c) => (
                    <label key={c.campaign_id} className="cfilter-item">
                      <Check
                        checked={draft.has(c.campaign_id)}
                        onChange={(on) => toggle([c.campaign_id], on)}
                      />
                      <span className="cid" title={name(c)}>
                        {name(c)}
                      </span>
                      <span className="dim cfilter-hint">
                        {c.recent_attempts > 0
                          ? `${compact(c.recent_attempts)} attempts · 30d`
                          : c.last_active
                            ? `idle since ${shortDate(c.last_active)}`
                            : "never ran"}
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="cfilter-foot">
            <span className="sub dim">
              {draft.size} of {all.length} selected
            </span>
            <button onClick={() => setOpen(false)}>Cancel</button>
            <button className="primary" disabled={draft.size === 0} onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const name = (c: MetricCampaign | undefined): string | undefined =>
  c && (c.campaign_identifier ?? c.campaign_name ?? c.campaign_id);

/** A checkbox that can also show "some of these". */
function Check({
  checked,
  indeterminate = false,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}
